import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import {
    CommentCategory,
    CommentCategoryRuntype,
    CommentEntityRuntype,
    CommentRuntype,
    CommentType,
} from './commentBackendTypes';

export const commentsEndpoints = {
    'POST /comments/bulk': endpoint({
        description: 'Fetches all the events associated with the given entity',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: CommentEntityRuntype,
        responseBody: z.object({
            comments: z.array(CommentRuntype),
        }),
        actionPermission: ({ type }: { type: CommentType }) => {
            switch (type) {
                case 'DesignItem':
                    return 'CanViewDesignItemComment';
                case 'Rfq':
                    return 'CanViewRfqComment';
                case 'Calculation':
                    return 'CanViewCalculationComment';
                case 'Assembly':
                    return 'CanViewAssemblyComment';
                case 'FlexibleCalculation':
                    return 'CanViewCalculationComment';
                // modules not yet implemented
                case 'QuoteRequest':
                    return undefined;
                case 'Monitoring':
                    return undefined;
            }
        },
    }),
    'PATCH /comments/:id': endpoint({
        description: 'Updates an event',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({ resolved: z.boolean() }),
        responseBody: z.unknown(),
        invalidates: ['POST /comments/bulk', 'GET /assemblies/:assemblyId/descendants', 'POST /design-items/history'],
        actionPermission: ({ type, newResolved }: { type: CommentType; newResolved: boolean | undefined }) => {
            if (newResolved === undefined) {
                return undefined;
            }
            switch (type) {
                case 'DesignItem':
                    return 'CanResolveDesignItemComment';
                case 'Rfq':
                    return 'CanResolveRfqComment';
                case 'Calculation':
                    return 'CanResolveCalculationComment';
                case 'Assembly':
                    return 'CanResolveAssemblyComment';
                case 'FlexibleCalculation':
                    return 'CanResolveCalculationComment';
                // modules not yet implemented
                case 'QuoteRequest':
                    return undefined;
                case 'Monitoring':
                    return undefined;
            }
        },
    }),
    'DELETE /comments/:id': endpoint({
        description: `Deletes an event given it's ID`,
        requestBody: z.undefined(),
        responseBody: z.unknown(),
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        invalidates: ['POST /comments/bulk', 'GET /assemblies/:assemblyId/descendants', 'POST /design-items/history'],
    }),
    'POST /comments': endpoint({
        description: `Creates an event`,
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            comment_entity: CommentEntityRuntype,
            content: z.string(),
            category: CommentCategoryRuntype,
        }),
        responseBody: z.unknown(),
        invalidates: ['POST /comments/bulk', 'GET /assemblies/:assemblyId/descendants', 'POST /design-items/history'],
        actionPermission: ({ type, category }: { type: CommentType; category: CommentCategory }) => {
            switch (type) {
                case 'DesignItem':
                    return category === 'Internal'
                        ? 'CanCreateInternalDesignItemComment'
                        : 'CanCreatePublicDesignItemComment';
                case 'Rfq':
                    return category === 'Internal' ? 'CanCreateInternalRfqComment' : 'CanCreatePublicRfqComment';
                case 'Calculation':
                    return 'CanCreateCalculationComment';
                case 'Assembly':
                    return category === 'Internal'
                        ? 'CanCreateInternalAssemblyComment'
                        : 'CanCreatePublicAssemblyComment';
                case 'FlexibleCalculation':
                    return 'CanCreateCalculationComment';
                // modules not yet implemented
                case 'QuoteRequest':
                    return undefined;
                case 'Monitoring':
                    return undefined;
            }
        },
    }),
    'POST /int/comments/:tenant/automated': endpoint({
        description: `Creates an automated comment via internal service-to-service communication`,
        pathParams: z.object({ tenant: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({
            comment_entity: CommentEntityRuntype,
            content: z.string(),
            category: CommentCategoryRuntype,
        }),
        responseBody: z.object({
            comment: CommentRuntype,
            comment_entity: CommentEntityRuntype,
        }),
        invalidates: ['POST /comments/bulk', 'GET /assemblies/:assemblyId/descendants', 'POST /design-items/history'],
    }),
};
